import { ActionIcon, Button, Checkbox, Group, NumberInput, Table, Text, TextInput } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import type { PagoItemEntrada } from '@serkat/shared'

interface Props {
  items: PagoItemEntrada[]
  errores: Record<string, string | undefined>
  onCambiar: (items: PagoItemEntrada[]) => void
}

/**
 * Detalle del LOG-FT-09: qué se pidió, qué llegó y si está conforme.
 * `Conforme` es la aceptación física de lo recibido, distinta del estado del
 * documento: se puede recibir todo y aun así observar la aprobación.
 */
export function TablaItemsPago({ items, errores, onCambiar }: Props) {
  const renumerar = (l: PagoItemEntrada[]) => l.map((it, i) => ({ ...it, itemNumero: i + 1 }))

  function agregar() {
    onCambiar(
      renumerar([
        ...items,
        {
          itemNumero: items.length + 1, descripcion: '', ordenItemId: null,
          cantidadSolicitada: 1, cantidadRecibida: 0, conforme: false,
        },
      ]),
    )
  }

  function editar(i: number, campo: keyof PagoItemEntrada, valor: unknown) {
    onCambiar(items.map((it, k) => (k === i ? { ...it, [campo]: valor } : it)))
  }

  return (
    <div>
      <Group justify="space-between" mb="xs">
        <Text fw={550} size="sm">Bienes o servicios recibidos</Text>
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={agregar}>
          Agregar ítem
        </Button>
      </Group>

      {errores['items'] && <Text c="red" size="xs" mb="xs">{errores['items']}</Text>}

      <Table.ScrollContainer minWidth={640}>
        <Table withTableBorder verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>#</Table.Th>
              <Table.Th>Descripción del bien / servicio</Table.Th>
              <Table.Th w={110}>Solicitada</Table.Th>
              <Table.Th w={110}>Recibida</Table.Th>
              <Table.Th w={90} ta="center">Conforme</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm" ta="center" py="sm">Sin ítems. Agregue al menos uno.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {items.map((it, i) => {
              const excede = it.cantidadRecibida > it.cantidadSolicitada
              return (
                <Table.Tr key={i}>
                  <Table.Td ta="center" c="dimmed">{it.itemNumero}</Table.Td>
                  <Table.Td>
                    <TextInput size="xs" value={it.descripcion}
                      onChange={(e) => editar(i, 'descripcion', e.currentTarget.value)}
                      error={Boolean(errores[`items.${i}.descripcion`])} />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput size="xs" min={0.0001} decimalScale={4} hideControls
                      value={it.cantidadSolicitada}
                      onChange={(v) => editar(i, 'cantidadSolicitada', Number(v) || 0)} />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput size="xs" min={0} decimalScale={4} hideControls
                      value={it.cantidadRecibida}
                      onChange={(v) => editar(i, 'cantidadRecibida', Number(v) || 0)}
                      error={excede ? 'Supera lo solicitado' : undefined} />
                  </Table.Td>
                  <Table.Td ta="center">
                    <Checkbox checked={it.conforme}
                      onChange={(e) => editar(i, 'conforme', e.currentTarget.checked)} />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="red"
                      onClick={() => onCambiar(renumerar(items.filter((_, k) => k !== i)))}>
                      <IconTrash size={15} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
